import cn from 'classnames'
import { PlausibleEvent } from 'enums/plausible'
import ContactsForm from 'forms/ContactsForm'
import { useEffect } from 'react'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import PageLayout from 'layouts/PageLayout'
import { getTitleLocation, MetaTypeNames } from 'pages'

import Container from 'components/Container/Container'
import SocialLinks from 'components/SocialLinks/SocialLinks'
import StructuredData from 'components/StructuredData/StructuredData'
import PaymentSystems from 'sections/PaymentSystems/PaymentSystems'

import styles from './contacts.module.scss'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const Contacts = ({
  referrer,
  ssrHost,
}: {
  referrer: string
  ssrHost: string
}) => {
  const [t] = useTranslation(['main', 'contacts'])
  const setEvent = usePlausible()

  useEffect(() => {
    setEvent(PlausibleEvent.OpenedContacts, {
      step: 1,
      referrer,
    })
  }, [])

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }

  return (
    <>
      <StructuredData
        host={ssrHost}
        variant="reduced"
        orgDetails
        path="/about/contacts"
        pageTitle={t(`seo:${getTitleLocation(ssrHost, 'contacts', MetaTypeNames.Title)}`)}
      />
      <Head>
        {/* t(`seo:contactsTitle`) */}
        {/* t(`seo:contactsTitle24`) */}
        <title>{t(`seo:${getTitleLocation(ssrHost, 'contacts', MetaTypeNames.Title)}`)}</title>
        {/* t(`seo:contactsDescription`) */}
        {/* t(`seo:contactsDescription24`) */}
        <meta
          name="description"
          content={t(`seo:${getTitleLocation(ssrHost, 'contacts', MetaTypeNames.Description)}`)}
        />

        <meta
          property="og:title"
          content={t(`seo:${getTitleLocation(ssrHost, 'contacts', MetaTypeNames.Title)}`)}
        />
        <meta
          property="og:description"
          content={t(`seo:${getTitleLocation(ssrHost, 'contacts', MetaTypeNames.Description)}`)}
        />
        <meta
          property="og:image"
          content={`https://${ssrHost}/images/ogImages/aboutContacts.png`}
        />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://${ssrHost}/about/contacts`} />
      </Head>

      <PageLayout
        disablePaddings
        subtitle={t('contacts:getFeedBack')}
        title={t('contacts:contactUs')}
        breadcrumbs={[
          { title: t('main:binanyTrading'), link: '/' },
          { title: t('contacts:contactUs'), link: '/about/contacts' },
        ]}
        host={ssrHost}
      >
        <Container>
          <div className={styles.grid}>
            <div className={cn(styles.card, styles.support)}>
              <div className={styles.supportMain}>
                <div className={styles.title}>{t('contacts:customerSupport')}</div>
                <div className={styles.description}>
                  <p>{t('contacts:feelFree')}</p>
                  <p>{t('contacts:ourStanby')}</p>
                  <p>{t('contacts:weValueTheTime')}</p>
                  <p>{t('contacts:feelTheVantage')}</p>
                  <ol>
                    <li>{t('contacts:learnWithUs')}</li>
                    <li>{t('contacts:growWithUs')}</li>
                    <li>{t('contacts:earnWithUs')}</li>
                  </ol>
                  <p>{t('contacts:helpUsTransform')}</p>
                </div>

                <div className={styles.international}>
                  <div className={styles.text}>E-mail</div>
                  <div className={styles.email}>
                    <a
                      id="about-contacts-support-link"
                      onClick={() =>
                        setEvent(PlausibleEvent.ClickedSendEmail, { step: 2, referrer })
                      }
                      href="mailto:support@binany.com"
                    >
                      support@binany.com
                    </a>
                  </div>
                </div>
              </div>
              <div className={styles.supportSecondary}>
                <div className={styles.socialTitle}>{t('contacts:weInSocialNetworks')}</div>
                <div className={styles.socialIcons}>
                  <SocialLinks referrer={referrer} pageName="about-contacts" fromContats />
                </div>
                <div className={styles.socialControl}>
                  <span className={styles.socialControlTitle}>{t('contacts:inAsia')}</span>
                  <span>
                    Binany, {new Date().getFullYear()}.
                    <br />
                    {`${t('main:footerRightsReserved')}. `}
                  </span>
                  <span>
                    {t('contacts:contactUs')}:
                    <br />
                    <a href="mailto:support@binany.com">support@binany.com</a>
                  </span>
                  <span> Finpulse LLC</span>
                </div>
              </div>
            </div>
            <div className={cn(styles.card, styles.feedback)}>
              <h3 className={styles.feedback_title}>{t('contacts:haveAQ')}</h3>
              <ContactsForm referrer={referrer} />
            </div>
            <div className={cn(styles.supportSecondary, styles.supportSecondaryMobile)}>
              <div className={styles.socialTitle}>{t('contacts:weInSocialNetworks')}</div>
              <div className={styles.socialIcons}>
                <SocialLinks referrer={referrer} fromContats pageName="about-contacts" />
              </div>
              <div className={styles.socialControl}>
                <span className={styles.socialControlTitle}>{t('contacts:inAsia')}</span>
                <span>
                  Binany, {new Date().getFullYear()}.
                  <br />
                  {`${t('main:footerRightsReserved')}. `}
                </span>
                <span>
                  {t('contacts:contactUs')}:
                  <br />
                  <a href="mailto:support@binany.com">support@binany.com</a>
                </span>
                <span> Finpulse LLC</span>
              </div>
            </div>
            <PaymentSystems className={styles.wrapper} />
          </div>
        </Container>
      </PageLayout>
    </>
  )
}

export default Contacts

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      ssrHost: req.headers.host || '',
      ...(await serverSideTranslations(locale, ['main', 'contacts', 'error', 'shared', 'seo'])),
    },
  }
}
